// Modules
import moment from "moment"
import { Link } from "react-router-dom"

// Constants
import PATHS, { getFullPath } from "~constants/paths"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Utils
import { isMessageActive } from "./utils/message"

// Components
import ListItemMenu, { ListItemMenuProps } from "~components/ListItemMenu"
import LoadingTable from "~components/LoadingTable"
import { CompanyLogo } from "~components/companies/styled/company"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { ActiveStatus, InactiveStatus } from "~styled/status-badget"
import {
  NoTableData,
  Table,
  TableContainer,
  TableLink,
  Td,
  Th,
  Tr,
} from "~components/styled/table"
import Pin from "@mui/icons-material/Bookmark"

// Interfaces
import { MessagePinLevel, MessageShape, MessageStatus } from "~shapes/message"
import { getFullImagePath } from "~utils/utils"
import { ButtonIcon } from "~styled/button"
import colors from "~constants/colors"
import { CompanyShape } from "~shapes/company"

const messageDetailPath = (id: string): string => {
  return `${getFullPath(PATHS.messages)}/${id}`
}

export interface MessagesListProps {
  company: CompanyShape
  messages: MessageShape[]
  isLoading: boolean
  pinLevel: MessagePinLevel
  onClickMessageEdit: (message: MessageShape) => void
  onChange: (message: Partial<MessageShape>) => void
  onClickView: (message: MessageShape) => void
}

const MessagesList = (props: MessagesListProps) => {
  const {
    messages,
    isLoading,
    onClickMessageEdit,
    onChange,
    onClickView,
    company,
    pinLevel,
  } = props

  const isFirstLoad: boolean = !messages.length && isLoading
  const noMessagesFound: boolean = !messages.length && !isLoading

  const statusIndicator = (message: MessageShape) => {
    return isMessageActive(message) ? <ActiveStatus /> : <InactiveStatus />
  }

  const handleToggleStatus = (message: MessageShape) => {
    if (message.id) {
      const status: MessageStatus =
        message.status === MessageStatus.ACTIVE
          ? MessageStatus.INACTIVE
          : MessageStatus.ACTIVE
      const dataToUpdate: Partial<MessageShape> = {
        id: message.id,
        status: status,
      }
      onChange(dataToUpdate)
    }
  }

  const handleTogglePinnedMessage = (message: MessageShape) => {
    if (message.id) {
      let dataToUpdate: Partial<MessageShape> = {
        id: message.id,
      }
      if (pinLevel == MessagePinLevel.COMMUNICATION_LEVEL) {
        dataToUpdate.communicationLevelPinned =
          !message.communicationLevelPinned
      } else {
        dataToUpdate.companyLevelPinned = !message.companyLevelPinned
      }
      onChange(dataToUpdate)
    }
  }

  const getSkeletonColumns = (): number => {
    return (checkAuthorization(RBAC_RULES.SEE_MESSAGE_COMPANY) && 7) || 6
  }

  const listMenuProps = message => {
    let props: Partial<ListItemMenuProps> = {}

    // View Permissions
    if (checkAuthorization(RBAC_RULES.VIEW_MESSAGE_DETAILS)) {
      props.onView = () => onClickView(message)
    }

    // Edit Permissions
    if (checkAuthorization(RBAC_RULES.UPDATE_MESSAGE)) {
      props.onEdit = () => onClickMessageEdit(message)
    }

    // Change Status Permissions
    if (checkAuthorization(RBAC_RULES.UPDATE_MESSAGE_STATUS)) {
      props.onToggleStatus = () => handleToggleStatus(message)
    }
    return props
  }

  let tdClassName, iconPinned

  return (
    <TableContainer>
      {/* Table */}
      <Table>
        <thead>
          <tr>
            <Th style={{ width: "40px" }} />
            <IsAuthorized action={RBAC_RULES.SEE_MESSAGE_COMPANY}>
              <>
                <Th style={{ width: "90px" }} />
                <Th style={{ width: "250px" }}>Company</Th>
              </>
            </IsAuthorized>
            <Th style={{ minWidth: "150px" }}>Message code</Th>
            <Th style={{ minWidth: "300px" }}>Message title</Th>
            <Th style={{ width: "120px" }}>Date</Th>
            <IsAuthorized action={RBAC_RULES.UPDATE_MESSAGE_PINNED}>
              <Th style={{ width: "120px" }}></Th>
            </IsAuthorized>
            <Th style={{ width: "80px" }} />
          </tr>
        </thead>

        <tbody>
          {isFirstLoad && (
            <LoadingTable rowHeight="75px" columns={getSkeletonColumns()}>
              <Td>
                <SkeletonWrapper isLoading={true} className="skeleton-status" />
              </Td>
              <IsAuthorized action={RBAC_RULES.SEE_MESSAGE_COMPANY}>
                <Td>
                  <SkeletonWrapper
                    isLoading={true}
                    className="skeleton-image"
                    width={60}
                  />
                </Td>
              </IsAuthorized>
            </LoadingTable>
          )}

          {messages &&
            messages.map((message: MessageShape) => {
              if (pinLevel == MessagePinLevel.COMMUNICATION_LEVEL) {
                tdClassName =
                  isMessageActive(message) && message.communicationLevelPinned
                    ? "pinned"
                    : ""
                iconPinned = message.communicationLevelPinned
              } else {
                tdClassName =
                  isMessageActive(message) && message.companyLevelPinned
                    ? "pinned"
                    : ""
                iconPinned = message.companyLevelPinned
              }
              return (
                <Tr key={message.id}>
                  {/* Status */}
                  <Td className={tdClassName}>
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-status"
                    >
                      {statusIndicator(message)}
                    </SkeletonWrapper>
                  </Td>

                  {/* Logo and Company Name */}
                  <IsAuthorized action={RBAC_RULES.SEE_MESSAGE_COMPANY}>
                    <>
                      {/* Logo */}
                      <Td className={tdClassName}>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-image"
                          width={60}
                        >
                          <CompanyLogo
                            src={
                              !!company
                                ? getFullImagePath(company.logo)
                                : getFullImagePath(message.company?.logo)
                            }
                          />
                        </SkeletonWrapper>
                      </Td>

                      {/* Company name */}
                      <Td className={tdClassName}>
                        <SkeletonWrapper isLoading={isLoading}>
                          <TableLink>
                            <Link to={messageDetailPath(message.id)}>
                              {!!company ? company.name : message.company?.name}
                            </Link>
                          </TableLink>
                        </SkeletonWrapper>
                      </Td>
                    </>
                  </IsAuthorized>

                  {/* Message code */}
                  <Td className={tdClassName}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <div style={{ maxWidth: "150px" }}>{message.code}</div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Message title */}
                  <Td className={tdClassName}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <div style={{ maxWidth: "300px" }}>{message.name}</div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Created */}
                  <Td className={tdClassName}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <div>
                        {moment(
                          message.publishedAt || message.createdAt,
                          "YYYY-MM-DD HH:mm:ss"
                        ).format("MM/DD/YYYY")}
                      </div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Pin */}
                  <IsAuthorized action={RBAC_RULES.UPDATE_MESSAGE_PINNED}>
                    <Td className={tdClassName}>
                      <SkeletonWrapper isLoading={isLoading}>
                        <ButtonIcon
                          onClick={() => handleTogglePinnedMessage(message)}
                        >
                          <Pin
                            style={{
                              color: iconPinned ? colors.blue2 : colors.white,
                              stroke: colors.black,
                            }}
                          />
                        </ButtonIcon>
                      </SkeletonWrapper>
                    </Td>
                  </IsAuthorized>

                  {/* Actions */}
                  <Td className={tdClassName}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <ListItemMenu
                        isActive={isMessageActive(message)}
                        {...listMenuProps(message)}
                      />
                    </SkeletonWrapper>
                  </Td>
                </Tr>
              )
            })}
        </tbody>
      </Table>

      {/* No results found */}
      {noMessagesFound && <NoTableData>No messages found...</NoTableData>}
    </TableContainer>
  )
}

export default MessagesList
